/**
 * The optional front-of-card photo. The check-in has already landed by the time any of this runs, so every
 * failure here is recoverable and none of it blocks entry. Nothing in here needs the scan's paths, fields or
 * response routing — the dependency only ever runs scan -> photo.
 *
 * Re-exported by `camera-id-scan.ts`, which stays the single import site for both surfaces.
 */

export type IdImage = {
  ImageBase64: string;
  MimeType: string;
};

/**
 * The opted-in front-of-card photo for the customer's profile, on Register.
 *
 * Register-only server-side (leaflogix #22416): the route is what the server authorizes on, so this
 * path is not a surface-agnostic one that a `Surface` field then narrows. Mobile checkout has its
 * own route below and must not post here.
 */
export const ID_PHOTO_PATH = 'checkin/id-ocr/photo';

/**
 * The same concern on mobile checkout, and a distinct route rather than the same one with a
 * different body.
 *
 * Declared as its own literal instead of derived from `ID_PHOTO_PATH` with a prefix: the server
 * owns both strings independently, and a computed path would let a change to Register's route
 * silently move Mobile's too. The client's job here is to name what the server serves, not to
 * model a relationship between the two.
 */
export const MOBILE_CHECKOUT_ID_PHOTO_PATH = 'checkin/mobile-checkout/id-ocr/photo';

export type IdPhotoPayload = {
  CustomerId: number;
  /** Named for the side: the server decodes a barcode out of it to refuse a photo of the back. */
  FrontImageBase64: string;
  MimeType: string;
};

export type IdPhotoResult = {
  CustomerId: number;
  Persisted: boolean;
  Failure: string | null;
};

/**
 * The `ApiResult` envelope this route answers in, pinned here because the photo route uses it
 * differently from every other call on this feature.
 *
 * A recoverable photo refusal — the back of the card — answers `Result: false` *with* a populated
 * `Data`. That is the server telling staff something they can fix, not a failed request. The
 * standard `post()` helper reads `Result` first and rejects with only `Message`, throwing `Data`
 * away, so the `Failure` code never reaches the step that owns the copy. Both surfaces therefore
 * ask for the raw envelope and unwrap it here.
 */
export type IdPhotoEnvelope = {
  Result: boolean;
  Message?: string | null;
  Data?: IdPhotoResult | null;
};

/**
 * A payload is a photo result when it says whether the image persisted. Deliberately structural and
 * deliberately not a truthiness check: `Persisted: false` is the whole point of a refusal, so the
 * only question is whether the server answered the question at all.
 */
const isIdPhotoResult = (data: unknown): data is IdPhotoResult =>
  typeof data === 'object' && data !== null && typeof (data as IdPhotoResult).Persisted === 'boolean';

/**
 * The photo result out of its envelope, whichever way `Result` went.
 *
 * `Result` is not consulted, and that is the correction: a wrong-side refusal is `Result: false`
 * carrying `Persisted: false` and `Failure: 'WrongSideOfDocument'`, and it has to reach the caller
 * as a resolved attempt so the step can show the one photo failure staff can actually correct.
 *
 * Three shapes are not that, and none of them may be read as a refusal:
 * - an envelope whose `Data` does not answer the question (`null`, absent, a body that did not
 *   deserialise) is a generic failure, because nothing in it says what happened to the image;
 * - no envelope at all, for the same reason;
 * - a transport rejection, which never reaches here at all — `post()` rejects first, and the caller
 *   keeps treating that as a failed request rather than something the server said.
 * For the first two this throws, which leaves the caller's existing catch on its retryable generic
 * path, and carries the server's `Message` on the error where there is one worth logging.
 */
export const unwrapIdPhotoResult = (envelope: IdPhotoEnvelope | null | undefined): IdPhotoResult => {
  if (isIdPhotoResult(envelope?.Data)) {
    return envelope.Data;
  }

  throw new Error(envelope?.Message?.trim() || ID_PHOTO_SAVE_FAILURE_MESSAGE);
};

/** Bigger than the ID thumbnail profile: the server decodes this to reject a photo of the back. */
export const ID_PHOTO_COMPRESSION_OPTIONS = { maxSizeMB: 0.6, maxWidthOrHeight: 1920 };

/**
 * `;base64` is required, not optional: a percent-encoded or plain data URL would otherwise match and its raw
 * text would reach the server as `FrontImageBase64`, which cannot be decoded. Case-insensitive because the
 * marker's casing is not guaranteed, and the media type keeps its own parameters.
 */
const DATA_URL_PATTERN = /^data:([^;,]+)(?:;[^;,]*)*;base64,([\s\S]+)$/i;

/**
 * Splits a base64 image data URL into the photo request's fields. Null for anything else.
 *
 * The media type is matched case-insensitively, like the pattern above and like the spec: media
 * types are case-insensitive, so `data:IMAGE/PNG;base64,...` parsing and then being refused was an
 * inconsistency, not a policy. It is lower-cased on the way out too, so the server never has to make
 * the same allowance when it compares the type it was sent.
 */
export const toIdImage = (dataUrl: string | null | undefined): IdImage | null => {
  const match = typeof dataUrl === 'string' ? DATA_URL_PATTERN.exec(dataUrl) : null;
  if (!match) {
    return null;
  }

  const mimeType = match[1].toLowerCase();

  return mimeType.startsWith('image/') ? { ImageBase64: match[2], MimeType: mimeType } : null;
};

/** The front-photo step, shared so both surfaces offer the same choice in the same words. Skipping is a first-class action, not a cancel. */
export const FRONT_PHOTO_HEADING = 'Photo of the front';
export const FRONT_PHOTO_HINT = "The side with the customer's photo. This is what gets saved to the profile.";
export const FRONT_PHOTO_CAMERA_ACTION = 'Use camera';
export const FRONT_PHOTO_UPLOAD_ACTION = 'Upload image';
export const FRONT_PHOTO_SKIP_ACTION = 'Skip';
export const FRONT_PHOTO_SHUTTER_ACTION = 'Take photo';

const FRONT_PHOTO_CAMERA_MESSAGES = {
  insecure: 'The camera needs a secure (https) connection. Upload the photo instead.',
  denied: 'Camera access is blocked for this site. Allow it in the browser address bar, or upload the photo.',
  notFound: 'No camera was found on this device. Upload the photo instead.',
  busy: 'The camera is already in use by another app. Upload the photo instead.',
  unknown: 'The camera could not be started. Upload the photo instead.',
};

/** Separate from `cameraErrorMessage`, which sends staff to upload the *barcode* — the wrong side here. */
export const frontPhotoCameraErrorMessage = (kind: keyof typeof FRONT_PHOTO_CAMERA_MESSAGES): string =>
  FRONT_PHOTO_CAMERA_MESSAGES[kind];

/** Only the optional profile photo failed. The check-in itself already happened. */
export const ID_PHOTO_SAVE_FAILURE_MESSAGE = 'The check-in is done, but the photo could not be saved.';

/** Staff photographed the barcode side — the one photo failure they can fix. */
export const ID_PHOTO_WRONG_SIDE_MESSAGE =
  'The check-in is done, but that photo was the back of the ID. Photograph the front to save it.';

const WRONG_SIDE_OF_DOCUMENT = 'WrongSideOfDocument';

export const toIdPhotoPayload = (customerId: number, image: IdImage): IdPhotoPayload => ({
  CustomerId: customerId,
  FrontImageBase64: image.ImageBase64,
  MimeType: image.MimeType,
});

export const photoSaveFailureMessage = (failure: string | null | undefined): string =>
  failure === WRONG_SIDE_OF_DOCUMENT ? ID_PHOTO_WRONG_SIDE_MESSAGE : ID_PHOTO_SAVE_FAILURE_MESSAGE;
